import { KP, KEYPOINT_COUNT } from '../../src/domain/pose/halpe26.ts';
import {
  packKeypoints,
  type FrameMessage,
  type PoseMessage,
  type ScoreMessage,
} from '../../src/domain/tv/protocol.ts';
import type { FrameState } from '../../src/domain/pose/frameState.ts';

/**
 * SAHTE SÜRÜCÜ — alıcıyı telefonsuz çalıştırır
 * ===========================================================================
 * Cast hesabı, TV, hatta telefon olmadan alıcının tamamını geliştirebilmek
 * için var. Gerçek gönderici ile AYNI mesajları üretir, dolayısıyla burada
 * çalışan bir şey telefona bağlandığında da çalışır.
 *
 * `?mock=0` ile kapatılır (gerçek taşıma katmanı bağlandığında).
 */

/** Ayakta duran bir insanın kaba iskeleti, 0..1 normalize. */
const BASE: Array<[number, number]> = (() => {
  const p = new Array<[number, number]>(KEYPOINT_COUNT).fill([0.5, 0.5]);
  p[KP.head] = [0.5, 0.09];
  p[KP.nose] = [0.5, 0.13];
  p[KP.leftEye] = [0.48, 0.12];
  p[KP.rightEye] = [0.52, 0.12];
  p[KP.leftEar] = [0.46, 0.13];
  p[KP.rightEar] = [0.54, 0.13];
  p[KP.neck] = [0.5, 0.2];
  p[KP.leftShoulder] = [0.41, 0.23];
  p[KP.rightShoulder] = [0.59, 0.23];
  p[KP.leftElbow] = [0.37, 0.36];
  p[KP.rightElbow] = [0.63, 0.36];
  p[KP.leftWrist] = [0.35, 0.49];
  p[KP.rightWrist] = [0.65, 0.49];
  p[KP.pelvis] = [0.5, 0.53];
  p[KP.leftHip] = [0.45, 0.54];
  p[KP.rightHip] = [0.55, 0.54];
  p[KP.leftKnee] = [0.44, 0.71];
  p[KP.rightKnee] = [0.56, 0.71];
  p[KP.leftAnkle] = [0.43, 0.88];
  p[KP.rightAnkle] = [0.57, 0.88];
  p[KP.leftHeel] = [0.42, 0.91];
  p[KP.rightHeel] = [0.58, 0.91];
  p[KP.leftBigToe] = [0.46, 0.93];
  p[KP.rightBigToe] = [0.54, 0.93];
  p[KP.leftSmallToe] = [0.4, 0.93];
  p[KP.rightSmallToe] = [0.6, 0.93];
  return p;
})();

/** Kolları sallayan, dizleri hafif büken basit bir dans döngüsü. */
const posture = (t: number): number[] => {
  const swing = Math.sin(t * 2.2);
  const bob = Math.sin(t * 4.4) * 0.012;
  const flat: number[] = [];
  for (let k = 0; k < KEYPOINT_COUNT; k += 1) {
    const base = BASE[k] ?? [0.5, 0.5];
    let [x, y] = base;
    y += bob;
    if (k === KP.leftElbow || k === KP.leftWrist) {
      const reach = k === KP.leftWrist ? 0.09 : 0.05;
      x -= swing * reach;
      y -= swing * reach * 1.6;
    }
    if (k === KP.rightElbow || k === KP.rightWrist) {
      const reach = k === KP.rightWrist ? 0.09 : 0.05;
      x += swing * reach;
      y += swing * reach * 1.6;
    }
    if (k === KP.leftKnee || k === KP.rightKnee) y += Math.abs(swing) * 0.02;
    flat.push(x, y, 0.9);
  }
  return flat;
};

type Sink = (message: unknown) => void;

/** Gerçek göndericinin hızıyla (12 Hz) sahte mesaj üretir. */
export const startMockSender = (sink: Sink): (() => void) => {
  const started = Date.now();
  let score = 0;
  let combo = 0;

  const poseTimer = window.setInterval(() => {
    const t = (Date.now() - started) / 1000;
    const message: PoseMessage = {
      t: 'pose',
      ts: Date.now(),
      p: packKeypoints(posture(t)),
    };
    sink(message);
  }, 1000 / 12);

  const scoreTimer = window.setInterval(() => {
    const roll = Math.random();
    const rating = roll > 0.75 ? 'perfect' : roll > 0.25 ? 'good' : 'miss';
    if (rating === 'miss') {
      combo = 0;
    } else {
      combo += 1;
      score += rating === 'perfect' ? 100 : 50;
    }
    const message: ScoreMessage = { t: 'score', total: score, combo, rating };
    sink(message);
  }, 900);

  const states: FrameState[] = ['in_frame', 'in_frame', 'in_frame', 'too_far'];
  let index = 0;
  const frameTimer = window.setInterval(() => {
    index = (index + 1) % states.length;
    const message: FrameMessage = {
      t: 'frame',
      state: states[index] ?? 'in_frame',
    };
    sink(message);
  }, 5000);

  return () => {
    window.clearInterval(poseTimer);
    window.clearInterval(scoreTimer);
    window.clearInterval(frameTimer);
  };
};
